<template>
  <b-card no-body class="rfb-container mb-3">
    <div class="rfb-bar">
      <div class="rfb-label fw-semibold small text-muted">
        <svg class="rfb-icon" viewBox="0 0 16 16" fill="currentColor"><path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/></svg>
        Filters
      </div>

      <b-button-group v-if="filters.length > 1" size="sm">
        <b-button :variant="matchMode === 'all' ? 'primary' : 'outline-secondary'" :aria-pressed="matchMode === 'all' ? 'true' : 'false'" @click="updateMatchMode('all')">All</b-button>
        <b-button :variant="matchMode === 'any' ? 'primary' : 'outline-secondary'" :aria-pressed="matchMode === 'any' ? 'true' : 'false'" @click="updateMatchMode('any')">Any</b-button>
      </b-button-group>

      <div class="rfb-pills">
        <b-badge v-for="(filter, idx) in filters" :key="idx" pill variant="secondary" class="rfb-pill" :class="'rfb-pill-' + filter.source">
          <span class="rfb-pill-field fw-semibold">{{ getFieldLabel(filter) }}</span>
          <span v-if="filter.relQuantifier && (filter.source === 'forward' || filter.source === 'reverse')" class="rfb-pill-quant">{{ filter.relQuantifier }}</span>
          <span class="rfb-pill-op opacity-75">{{ getOperatorLabel(filter.operator) }}</span>
          <span v-if="!isNoValueOp(filter.operator)" class="rfb-pill-val" :class="{ 'fst-italic': filter.valueType === 'field' }">{{ formatDisplayValue(filter) }}</span>
          <b-button variant="close" class="ms-1 rfb-pill-close" @click="removeFilter(idx)" title="Remove" aria-label="Remove"></b-button>
        </b-badge>
      </div>

      <b-button size="sm" variant="outline-primary" @click="toggleForm">
        <span v-if="!isAdding">+ Add filter</span>
        <span v-else>Cancel</span>
      </b-button>
    </div>

    <div v-if="isAdding" class="rfb-form border-top p-2">
      <div class="rfb-inline-row">
        <div class="rfb-inline-field">
          <b-form-select v-model="newFieldKey" aria-label="Field" @change="onFieldChange" size="sm">
            <option value="" disabled>Field...</option>
            <optgroup v-for="group in fieldGroups" :key="group.key" :label="group.label">
              <option v-for="f in group.fields" :key="f.key" :value="f.key">{{ f.displayName }}</option>
            </optgroup>
          </b-form-select>
        </div>

        <b-button-group v-if="newFieldKey && selectedFieldIsRelationship" size="sm">
          <b-button :variant="newRelQuantifier === 'any' ? 'primary' : 'outline-secondary'" :aria-pressed="newRelQuantifier === 'any' ? 'true' : 'false'" @click="newRelQuantifier = 'any'" title="At least one related record meets the condition">any</b-button>
          <b-button :variant="newRelQuantifier === 'all' ? 'primary' : 'outline-secondary'" :aria-pressed="newRelQuantifier === 'all' ? 'true' : 'false'" @click="newRelQuantifier = 'all'" title="Every related record must meet the condition">all</b-button>
        </b-button-group>

        <div v-if="newFieldKey" class="rfb-inline-field rfb-inline-op">
          <b-form-select v-model="newOperator" aria-label="Operator" size="sm">
            <option value="" disabled>Operator...</option>
            <option v-for="op in operatorsForSelectedField" :key="op.key" :value="op.key">{{ op.label }}</option>
          </b-form-select>
        </div>

        <template v-if="newOperator && !isNoValueOp(newOperator)">
          <template v-if="selectedFieldDataType === 'relationship' && isRelValueOp(newOperator)">
            <div class="rfb-rel-picker">
              <div v-if="relSelectedRecords.length" class="rfb-rel-tags">
                <b-badge v-for="rec in relSelectedRecords" :key="rec.id" variant="info" pill class="rfb-rel-tag">
                  {{ rec.displayValue }}
                  <button class="btn-close btn-close-sm ms-1" @click="removeRelRecord(rec.id)" aria-label="Remove"></button>
                </b-badge>
              </div>
              <div class="rfb-rel-search-wrap">
                <b-form-input type="text" v-model="relSearchQuery" aria-label="Search records" placeholder="Search records..." @input="onRelSearchInput" size="sm" />
                <div v-if="relSearchLoading" class="text-muted fst-italic small mt-1">searching...</div>
              </div>
              <div v-if="relSearchResults.length" class="rfb-rel-results border rounded mt-1">
                <button type="button" v-for="rec in relSearchResults" :key="rec.id" class="rfb-rel-result-item" @click="selectRelRecord(rec)">
                  {{ rec.displayValue || '(no value)' }}
                </button>
              </div>
            </div>
          </template>
          <template v-else-if="selectedFieldDataType === 'number'">
            <b-button-group size="sm">
              <b-button :variant="newValueType === 'literal' ? 'primary' : 'outline-secondary'" :aria-pressed="newValueType === 'literal' ? 'true' : 'false'" @click="setValueType('literal')">val</b-button>
              <b-button :variant="newValueType === 'field' ? 'primary' : 'outline-secondary'" :aria-pressed="newValueType === 'field' ? 'true' : 'false'" @click="setValueType('field')">field</b-button>
            </b-button-group>
            <div class="rfb-inline-field rfb-inline-val">
              <b-form-input v-if="newValueType === 'literal'" type="number" v-model="newValue" aria-label="Value" placeholder="Number..." @keyup.enter="confirmAdd" size="sm" />
              <b-form-select v-else v-model="newValueFieldKey" size="sm">
                <option value="" disabled>Numeric field...</option>
                <optgroup v-for="group in numericFieldGroups" :key="group.key" :label="group.label">
                  <option v-for="f in group.fields" :key="f.key" :value="f.key">{{ f.displayName }}</option>
                </optgroup>
              </b-form-select>
            </div>
          </template>
          <div v-else-if="selectedFieldDataType === 'datetime'" class="rfb-inline-field rfb-inline-val">
            <b-form-input type="datetime-local" v-model="newValue" aria-label="Value" size="sm" />
          </div>
          <div v-else class="rfb-inline-field rfb-inline-val">
            <b-form-input type="text" v-model="newValue" aria-label="Value" placeholder="Value..." @keyup.enter="confirmAdd" size="sm" />
          </div>
        </template>

        <b-button size="sm" variant="primary" :disabled="!isFormValid" @click="confirmAdd">Apply</b-button>
      </div>
    </div>
  </b-card>
</template>

<script>
const OPERATORS = {
  string: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'does not equal' },
    { key: 'contains', label: 'contains' },
    { key: 'begins', label: 'starts with' },
    { key: 'ends', label: 'ends with' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  number: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'does not equal' },
    { key: '<', label: 'less than' },
    { key: '<=', label: 'less than or equal' },
    { key: '>', label: 'greater than' },
    { key: '>=', label: 'greater than or equal' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  datetime: [
    { key: '=', label: 'equals' },
    { key: '<', label: 'is before' },
    { key: '<=', label: 'is on or before' },
    { key: '>', label: 'is after' },
    { key: '>=', label: 'is on or after' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  relationship: [
    { key: 'is', label: 'is' },
    { key: 'isNot', label: 'is not' },
    { key: 'includes', label: 'includes' },
    { key: 'doesNotInclude', label: 'does not include' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  signature: [
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ]
}

const REL_VALUE_OPS = new Set(['is', 'isNot', 'includes', 'doesNotInclude'])

export default {
  name: 'RuntimeFilterBuilder',
  props: {
    fieldGroups: { type: Array, default: () => [] },
    initialFilters: { type: Array, default: () => [] },
    initialMatchMode: { type: String, default: 'all' }
  },
  emits: ['filter-changed'],
  data () {
    return {
      filters: [],
      matchMode: 'all',
      hasAppliedInitial: false,
      isAdding: false,
      newFieldKey: '',
      newOperator: '',
      newValue: '',
      newValueType: 'literal',
      newValueFieldKey: '',
      newRelQuantifier: 'any',
      relSearchQuery: '',
      relSearchResults: [],
      relSearchLoading: false,
      relSelectedRecords: [],
      relSearchTimeout: null
    }
  },
  watch: {
    fieldGroups: {
      immediate: true,
      handler (groups) {
        if (this.hasAppliedInitial || !groups || !groups.length) return
        if (this.initialFilters && this.initialFilters.length > 0) {
          this.filters = JSON.parse(JSON.stringify(this.initialFilters))
          this.matchMode = this.initialMatchMode || 'all'
          this.hasAppliedInitial = true
          this.emitChange()
        }
      }
    }
  },
  computed: {
    allFieldOptions () {
      const flat = []
      for (const group of this.fieldGroups) {
        for (const f of group.fields) flat.push(f)
      }
      return flat
    },
    selectedField () {
      if (!this.newFieldKey) return null
      return this.allFieldOptions.find(f => f.key === this.newFieldKey) || null
    },
    selectedFieldDataType () {
      return this.selectedField ? this.selectedField.dataType : null
    },
    selectedFieldIsRelationship () {
      if (!this.selectedField) return false
      return this.selectedField.source === 'forward' || this.selectedField.source === 'reverse'
    },
    operatorsForSelectedField () {
      if (!this.selectedFieldDataType) return []
      return OPERATORS[this.selectedFieldDataType] || OPERATORS.string
    },
    numericFieldGroups () {
      return this.fieldGroups
        .map(g => ({ ...g, fields: g.fields.filter(f => f.dataType === 'number') }))
        .filter(g => g.fields.length > 0)
    },
    selectedValueField () {
      if (!this.newValueFieldKey) return null
      return this.allFieldOptions.find(f => f.key === this.newValueFieldKey) || null
    },
    isFormValid () {
      if (!this.newFieldKey || !this.newOperator) return false
      if (this.isNoValueOp(this.newOperator)) return true
      if (this.selectedFieldDataType === 'relationship') return this.relSelectedRecords.length > 0
      if (this.selectedFieldDataType === 'number' && this.newValueType === 'field') return !!this.newValueFieldKey
      if (!this.newValue && this.newValue !== 0) return false
      return true
    }
  },
  methods: {
    getFieldLabel (filter) {
      if (filter.source === 'forward' || filter.source === 'reverse') {
        const targetSS = filter.relatedSpreadsheet || filter.sourceSpreadsheet
        return `${targetSS} › ${filter.field}`
      }
      return filter.field
    },
    getOperatorLabel (key) {
      for (const ops of Object.values(OPERATORS)) {
        const found = ops.find(o => o.key === key)
        if (found) return found.label
      }
      return key
    },
    isNoValueOp (key) {
      return key === 'empty' || key === 'notEmpty'
    },
    formatDisplayValue (filter) {
      if (filter.valueType === 'field' && filter.valueFieldRef) return this.getFieldLabel(filter.valueFieldRef)
      if (filter.dataType === 'relationship' && Array.isArray(filter.value)) {
        return filter.value.map(r => r.displayValue || r.id).join(', ')
      }
      if (filter.dataType === 'number') return filter.value
      if (filter.dataType === 'datetime' && filter.value) {
        try { const d = new Date(filter.value); if (!isNaN(d.getTime())) return d.toLocaleString() } catch {}
      }
      return `"${filter.value}"`
    },
    onFieldChange () {
      this.newOperator = ''
      this.newValue = ''
      this.newValueType = 'literal'
      this.newValueFieldKey = ''
      this.newRelQuantifier = 'any'
      this.relSearchQuery = ''
      this.relSearchResults = []
      this.relSelectedRecords = []
    },
    setValueType (type) {
      this.newValueType = type
      if (type === 'literal') this.newValueFieldKey = ''
      else this.newValue = ''
    },
    toggleForm () {
      this.isAdding = !this.isAdding
      if (this.isAdding) {
        this.newFieldKey = ''
        this.newOperator = ''
        this.newValue = ''
        this.newValueType = 'literal'
        this.newValueFieldKey = ''
        this.newRelQuantifier = 'any'
        this.relSearchQuery = ''
        this.relSearchResults = []
        this.relSelectedRecords = []
      }
    },
    confirmAdd () {
      if (!this.isFormValid) return
      const field = this.selectedField
      const isFieldRef = this.selectedFieldDataType === 'number' && this.newValueType === 'field'
      const isRelDataType = this.selectedFieldDataType === 'relationship'
      const isRel = field.source === 'forward' || field.source === 'reverse'

      let filterValue
      if (this.isNoValueOp(this.newOperator)) {
        filterValue = ''
      } else if (isRelDataType) {
        filterValue = [...this.relSelectedRecords]
      } else if (isFieldRef) {
        filterValue = ''
      } else {
        filterValue = this.newValue
      }

      const newFilter = {
        field: field.field,
        source: field.source,
        relationshipColumn: field.relationshipColumn || null,
        relatedSpreadsheet: field.relatedSpreadsheet || null,
        relatedSpreadSheet: field.relatedSpreadSheet || null,
        sourceSpreadsheet: field.sourceSpreadsheet || null,
        sourceRelationshipColumn: field.sourceRelationshipColumn || null,
        dataType: field.dataType,
        relQuantifier: isRel ? this.newRelQuantifier : null,
        operator: this.newOperator,
        valueType: isFieldRef ? 'field' : 'literal',
        value: filterValue,
        valueFieldRef: null
      }

      if (isFieldRef && this.selectedValueField) {
        const vf = this.selectedValueField
        newFilter.valueFieldRef = {
          field: vf.field, source: vf.source, dataType: vf.dataType,
          relationshipColumn: vf.relationshipColumn || null,
          relatedSpreadsheet: vf.relatedSpreadsheet || null,
          sourceSpreadsheet: vf.sourceSpreadsheet || null,
          sourceRelationshipColumn: vf.sourceRelationshipColumn || null
        }
      }

      this.filters.push(newFilter)
      this.isAdding = false
      this.emitChange()
    },

    isRelValueOp (key) {
      return REL_VALUE_OPS.has(key)
    },

    onRelSearchInput () {
      if (this.relSearchTimeout) clearTimeout(this.relSearchTimeout)
      this.relSearchTimeout = setTimeout(() => this.searchRelRecords(), 250)
    },

    async searchRelRecords () {
      const field = this.selectedField
      const relSS = field ? (field.relatedSpreadSheet || null) : null
      const owner = field && field.ownerSpreadSheet
      const useOptions = field && field.dataType === 'relationship' && field.source === 'direct' && owner
      if (!useOptions && !relSS) return

      this.relSearchLoading = true
      try {
        const url = useOptions
          ? `${VUE_APP_PROXY_SERVER_URL}/data/${owner}/relationships/${field.field}/options?forLink=0&q=${encodeURIComponent(this.relSearchQuery)}`
          : `${VUE_APP_PROXY_SERVER_URL}/data/${relSS}/search?q=${encodeURIComponent(this.relSearchQuery)}`
        const headers = {}
        if (this.$route && this.$route.params && this.$route.params.application) {
          headers['application-unique-title'] = this.$route.params.application
        }
        const response = await fetch(url, { headers })
        const data = await response.json()
        const rows = Array.isArray(data) ? data : (data.options || [])
        const selectedIds = new Set(this.relSelectedRecords.map(r => r.id))
        this.relSearchResults = rows
          .map(row => ({ id: row.id, displayValue: row.displayValue || row.label || String(row.id) }))
          .filter(r => !selectedIds.has(r.id))
      } catch (err) {
        console.error('relationship search error:', err)
        this.relSearchResults = []
      } finally {
        this.relSearchLoading = false
      }
    },

    selectRelRecord (rec) {
      if (!this.relSelectedRecords.find(r => r.id === rec.id)) {
        this.relSelectedRecords.push({ id: rec.id, displayValue: rec.displayValue })
      }
      this.relSearchResults = this.relSearchResults.filter(r => r.id !== rec.id)
      this.relSearchQuery = ''
    },

    removeRelRecord (id) {
      this.relSelectedRecords = this.relSelectedRecords.filter(r => r.id !== id)
    },
    removeFilter (idx) {
      this.filters.splice(idx, 1)
      this.emitChange()
    },
    updateMatchMode (mode) {
      this.matchMode = mode
      this.emitChange()
    },
    emitChange () {
      this.$emit('filter-changed', { filters: this.filters, matchMode: this.matchMode })
    }
  }
}
</script>

<style scoped>
.rfb-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  flex-wrap: wrap;
}
.rfb-label {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}
.rfb-icon {
  width: 14px;
  height: 14px;
  opacity: 0.6;
}

.rfb-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.rfb-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  max-width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}
.rfb-pill-direct { border-left: 3px solid var(--bs-success) !important; }
.rfb-pill-forward { border-left: 3px solid var(--bs-warning) !important; }
.rfb-pill-reverse { border-left: 3px solid var(--bs-purple, #6f42c1) !important; }
.rfb-pill-field {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}
.rfb-pill-quant {
  font-size: 0.65rem;
  text-transform: uppercase;
}
.rfb-pill-op {
  white-space: nowrap;
}
.rfb-pill-val {
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.rfb-pill-close {
  font-size: 0.5rem;
  filter: invert(1) grayscale(100%) brightness(200%);
}

.rfb-inline-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.rfb-inline-field { min-width: 120px; }
.rfb-inline-op { min-width: 110px; }
.rfb-inline-val { min-width: 100px; flex: 1; }

.rfb-rel-picker {
  min-width: 180px;
  flex: 1;
}
.rfb-rel-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}
.rfb-rel-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.rfb-rel-results {
  max-height: 150px;
  overflow-y: auto;
}
button.rfb-rel-result-item {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  color: inherit;
}
.rfb-rel-result-item {
  padding: 5px 10px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.1s;
}
.rfb-rel-result-item:hover {
  opacity: 0.8;
  background: var(--bs-tertiary-bg, rgba(0, 0, 0, 0.05));
}
</style>
